import { type HighlightsRenderer } from './highlights-renderer.ts';
import {
  createDomHighlightsRenderingStrategy,
  type CreateHighlightsRenderingStrategy,
  type HighlightsRenderingStrategy
} from './highlights-rendering-strategy.ts';
import { type GeometryResolver } from './types.ts';

export interface HighlightsControllerOptions {
  id: string;
  /**
   * Invalidates geometry owned outside the controller before a layout redraw.
   * Omit when the injected resolver has no cached layout state.
   */
  invalidateGeometry?: () => void;
  /**
   * Supplies the layout-owned element whose width invalidates geometry.
   * The resolver's scroll container is used when this is omitted or returns null.
   */
  getResizeObserveTarget?: () => HTMLElement | null;
  /**
   * Replaces the default single-surface DOM renderer at the application
   * composition root. Feature consumers continue to call only `createLayer()`.
   */
  createRenderingStrategy?: CreateHighlightsRenderingStrategy;
}

class ControllerManagedHighlightsRenderer implements HighlightsRenderer {
  #renderer: HighlightsRenderer;
  #onDispose: () => void;
  #disposed = false;

  constructor(renderer: HighlightsRenderer, onDispose: () => void) {
    this.#renderer = renderer;
    this.#onDispose = onDispose;
  }

  add: HighlightsRenderer['add'] = (options) => this.#renderer.add(options);
  update: HighlightsRenderer['update'] = (range, mode) =>
    this.#renderer.update(range, mode);
  hideAll: HighlightsRenderer['hideAll'] = () => this.#renderer.hideAll();
  showAll: HighlightsRenderer['showAll'] = () => this.#renderer.showAll();
  redrawAll: HighlightsRenderer['redrawAll'] = () => this.#renderer.redrawAll();
  remove: HighlightsRenderer['remove'] = (id) => this.#renderer.remove(id);
  clear: HighlightsRenderer['clear'] = () => this.#renderer.clear();

  dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;
    this.#renderer.dispose();
    this.#onDispose();
  }
}

/**
 * Layout-neutral facade for mounting, creating, invalidating, and disposing
 * logical highlight layers.
 *
 * The controller owns one rendering strategy selected at construction time.
 * Feature consumers never select physical surfaces: they call `createLayer()`
 * and use the returned `HighlightsRenderer` regardless of the active layout.
 */
export class HighlightsController {
  #container: HTMLElement;
  #layers = new Set<ControllerManagedHighlightsRenderer>();
  #resolver: GeometryResolver;
  #invalidateGeometryCallback: () => void;
  #getResizeObserveTarget: () => HTMLElement | null;
  #renderingStrategy: HighlightsRenderingStrategy;

  #resizeObserver: ResizeObserver | null = null;
  #lastWidth: number | null = null;
  #disposed = false;

  constructor(resolver: GeometryResolver, options: HighlightsControllerOptions) {
    this.#resolver = resolver;
    this.#invalidateGeometryCallback = options.invalidateGeometry ?? (() => {});
    this.#getResizeObserveTarget = options.getResizeObserveTarget ?? (() => null);

    this.#container = document.createElement('div');
    this.#container.id = options.id;
    this.#container.style.position = 'absolute';
    this.#container.style.top = '0';
    this.#container.style.left = '0';
    this.#container.style.pointerEvents = 'none';

    const createRenderingStrategy =
      options.createRenderingStrategy ?? createDomHighlightsRenderingStrategy;

    this.#renderingStrategy = createRenderingStrategy({
      container: this.#container,
      resolver: this.#resolver,
      invalidateGeometry: () => this.invalidateGeometry()
    });
  }

  /**
   * Attaches the highlights container to the scroll container.
   * `createLayer` calls this automatically when a scroll parent exists. The
   * target injected at construction is used unless `resizeTarget` is provided.
   */
  public mount(resizeTarget?: HTMLElement | null) {
    const parent = this.#resolver.getScrollContainer();

    if (!parent || this.#container.parentElement === parent) {
      return;
    }

    parent.style.position = 'relative';
    parent.appendChild(this.#container);

    const targetToObserve = resizeTarget || parent;

    this.#lastWidth = targetToObserve.clientWidth;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => {
      const newWidth = targetToObserve.clientWidth;

      if (newWidth !== this.#lastWidth) {
        this.#lastWidth = newWidth;
        this.invalidateGeometry();
      }

      this.redrawAll();
    });

    this.#resizeObserver.observe(targetToObserve);
  }

  /**
   * Creates one logical layer. The injected rendering strategy privately
   * decides how many physical DOM surfaces are required for each highlight.
   */
  public createLayer(): HighlightsRenderer {
    if (this.#disposed) {
      throw new Error('Cannot create a highlight layer from a disposed controller.');
    }

    this.#ensureMountedBeforeLayer();

    const managedLayer = new ControllerManagedHighlightsRenderer(
      this.#renderingStrategy.createLayer(),
      () => {
        this.#layers.delete(managedLayer);

        if (this.#layers.size === 0) {
          this.#detachResizeObserverAndContainer();
        }
      }
    );

    this.#layers.add(managedLayer);

    return managedLayer;
  }

  /**
   * Completely tears down the controller and its rendering strategy.
   */
  public dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;

    for (const layer of [...this.#layers]) {
      layer.dispose();
    }

    this.#layers.clear();
    this.#renderingStrategy.dispose();
    this.#detachResizeObserverAndContainer();
  }

  /**
   * Returns the scroll container which is possibly null if the React app has
   * not mounted.
   */
  public getScrollContainer(): HTMLElement | null {
    return this.#resolver.getScrollContainer();
  }

  /**
   * Invalidates external geometry state supplied at construction time.
   */
  public invalidateGeometry() {
    this.#invalidateGeometryCallback();
  }

  /**
   * Redraws every logical highlight layer using the current layout geometry.
   */
  public redrawAll() {
    this.#layers.forEach((layer) => layer.redrawAll());
  }

  /**
   * Returns the controller-owned base overlay root.
   */
  public getContainer(): HTMLElement {
    return this.#container;
  }

  #ensureMountedBeforeLayer() {
    const parent = this.#resolver.getScrollContainer();

    if (parent && this.#container.parentElement !== parent) {
      this.mount(this.#getResizeObserveTarget());
    }
  }

  #detachResizeObserverAndContainer() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#lastWidth = null;
    this.#container.remove();
  }
}
